import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import logoIcon from "@assets/icons/logo_icon.png";
import Button from "@common/Button";

const NAV_LINKS = [
  { label: "Home",           href: "/",        scroll: "home"    },
  { label: "About Us",       href: "/about",   scroll: null      },
  { label: "Contact Us",     href: "/contact", scroll: null      },
  { label: "Privacy Policy", href: "/privacy", scroll: null      },
];

export default function Navbar({ solid = false }) {
  const [scrolled,  setScrolled]  = useState(solid);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(solid || window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, link) => {
    if (link.scroll) {
      e.preventDefault();
      const el = document.getElementById(link.scroll);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      else { navigate("/"); setTimeout(() => document.getElementById(link.scroll)?.scrollIntoView({ behavior: "smooth" }), 100); }
    }
    setMenuOpen(false);
  };

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "#FFFFFF" : "transparent",
      backdropFilter: scrolled ? "blur(12px)" : "none",
      boxShadow: scrolled ? "0 1px 0 #E5E7EB, 0 4px 20px rgba(0,0,0,0.06)" : "none",
      transition: "all 0.3s ease",
      padding: "0 24px",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 68 }}>

        {/* Logo */}
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }}>
          <img src={logoIcon} alt="FC" style={{ width: 30, height: 30, objectFit: "contain", flexShrink: 0 }} />
          <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.02em", color: scrolled ? "#111827" : "#fff", transition: "color 0.3s", whiteSpace: "nowrap" }}>
            Firm<span style={{ color: scrolled ? "#2563EB" : "#60A5FA" }}>Connector</span>
          </span>
        </Link>

        {/* Desktop links */}
        <div className="d-none d-lg-flex align-items-center gap-1">
          {NAV_LINKS.map(link => (
            <Link key={link.label} to={link.href}
              onClick={(e) => handleNav(e, link)}
              style={{ color: scrolled ? "#374151" : "rgba(255,255,255,0.85)", fontWeight: 500, fontSize: 14, padding: "6px 14px", borderRadius: 8, textDecoration: "none", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.color = scrolled ? "#2563EB" : "#fff"; e.currentTarget.style.background = scrolled ? "#EFF6FF" : "rgba(255,255,255,0.1)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = scrolled ? "#374151" : "rgba(255,255,255,0.85)"; e.currentTarget.style.background = "transparent"; }}>
              {link.label}
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="d-none d-lg-flex align-items-center gap-2">
          {isAuthenticated ? (
            <>
              <Link to="/dashboard"
                className="fc-btn fc-btn-sm"
                style={{ borderRadius: 999, border: scrolled ? "1.5px solid #E5E7EB" : "1.5px solid rgba(255,255,255,0.25)", color: scrolled ? "#2563EB" : "#E5E7EB", background: scrolled ? "#EFF6FF" : "rgba(255,255,255,0.08)", fontWeight: 600, padding: "7px 16px" }}>
                <i className="fa-solid fa-gauge-high" style={{ fontSize: 11 }} />
                Dashboard
              </Link>
              <button onClick={logout}
                style={{ display:"inline-flex", alignItems:"center", gap:7, padding:"7px 18px", borderRadius:99, background:"linear-gradient(135deg,#2563EB,#7C3AED)", color:"#fff", fontWeight:700, fontSize:13, border:"none", cursor:"pointer", fontFamily:"inherit", boxShadow:"0 4px 12px rgba(79,70,229,0.3)" }}>
                <i className="fa-solid fa-right-from-bracket" style={{fontSize:11}} /> Logout
              </button>
            </>
          ) : (
            <Link to="/login" className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
              <i className="fa-solid fa-right-to-bracket" style={{ fontSize: 11 }} />
              Sign In
            </Link>
          )}
        </div>

        {/* Mobile hamburger */}
        <button className="d-lg-none" onClick={() => setMenuOpen(!menuOpen)}
          style={{ background: "none", border: "none", color: scrolled ? "#374151" : "#fff", cursor: "pointer", fontSize: 20, padding: 4 }}>
          <i className={`fa-solid fa-${menuOpen ? "xmark" : "bars"}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{ background: "#111827", borderTop: "1px solid rgba(255,255,255,0.08)", padding: "12px 24px 20px" }}>
          {NAV_LINKS.map(link => (
            <Link key={link.label} to={link.href}
              onClick={(e) => handleNav(e, link)}
              style={{ display: "block", padding: "12px 0", color: "rgba(255,255,255,0.8)", fontWeight: 500, fontSize: 15, textDecoration: "none", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              {link.label}
            </Link>
          ))}
          <div className="mt-3 d-flex gap-2">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="fc-btn fc-btn-md fc-btn-ghost justify-content-center" style={{ flex:1, background:"transparent", color: "#E5E7EB", borderColor: "rgba(255,255,255,0.2)" }} onClick={() => setMenuOpen(false)}>
                  Dashboard
                </Link>
                <Button variant="primary" size="md" style={{ flex:1 }} onClick={() => { logout(); setMenuOpen(false); }}>Logout</Button>
              </>
            ) : (
              <Link to="/login" className="fc-btn fc-btn-md fc-btn-primary w-100 justify-content-center" onClick={() => setMenuOpen(false)}>
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
